import { PlanCard } from '@aibees/shared/features/plan-card';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { ChangeDuration } from '../change-duration';
import { CollapsibleSection } from '../collapsible-section';
import { PlanCellValue } from '../plan-cell-value';
import {
  getColumnKey,
  isFreePlanSet,
  productFamilyKey,
} from '../pricing-cards/catalog';
import { usePlanConfigs } from '../pricing-cards/hooks/use-plan-configs';
import * as SX from './sx';
import type { CollapsedTableProps as Props } from './types';
import { useCollapsedTableData } from './use-collapsed-table-data';

export const CollapsedTable = memo(({ className, ariaLabel }: Props) => {
  const { products, rows, plansByProduct, yearly } = useCollapsedTableData();
  const { byId, byFamily } = usePlanConfigs();
  return (
    <TableContainer
      className={classnames('CollapsedTable', className)}
      sx={SX.tableContainer}
    >
      <Table aria-label={ariaLabel} sx={SX.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={SX.headerFeatureCell}>
              <Stack sx={SX.comparePlansCell} gap={11}>
                <Stack
                  gap={1.5}
                  mt={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="h5" fontWeight={700} color="grey.800">
                    Compare Plans
                  </Typography>
                  <ChangeDuration />
                </Stack>
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Divider sx={SX.divider} flexItem />
                  <Box>
                    <Typography
                      variant="body5"
                      color="grey.500"
                      component="span"
                    >
                      Need a Custom Solution?{' '}
                    </Typography>
                    <Link
                      href="#"
                      underline="always"
                      variant="body5"
                      fontWeight={600}
                      color="primary.main"
                    >
                      Book a Demo
                    </Link>
                  </Box>
                </Stack>
              </Stack>
            </TableCell>
            {products.map((product) => {
              const isMostPopular = product.metadata?.label === 'MOST_POPULAR';
              const plans = plansByProduct[product.id] ?? [];
              // Same derivation as the pricing cards — a hardcoded `slug ===
              // FREE` would disagree with them for any other free product.
              const isFree = isFreePlanSet(plans);
              // `product` is from the `return=features` shape, the configs from
              // `return=cards`; resolve by id, then by the backend's own
              // family/slug grouping. No Free-config fallback — it would render
              // free copy on a paid column.
              const family = productFamilyKey(product);
              const config =
                byId[product.id] ?? (family ? byFamily[family] : undefined);

              return (
                <TableCell
                  key={product.id}
                  align="center"
                  sx={SX.headerPlanCell}
                >
                  <PlanCard
                    plans={plans}
                    yearly={yearly}
                    isFree={isFree}
                    config={config}
                    variant="compact"
                    isMostPopular={isMostPopular}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((item, index) =>
            item.kind === 'section' ? (
              <CollapsibleSection
                key={item.section.id}
                section={item.section}
                products={products}
                isLast={index === rows.length - 1}
              />
            ) : (
              <TableRow
                key={item.row.id}
                sx={index === rows.length - 1 ? SX.lastFlatRow : SX.featureRow}
              >
                <TableCell sx={SX.topRowFeatureCell}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                    noWrap
                  >
                    {item.row.feature}
                  </Typography>
                  {item.row.badge && (
                    <Typography variant="body5" color="grey.500">
                      {item.row.badge}
                    </Typography>
                  )}
                </TableCell>
                {products.map((product) => {
                  const columnKey = getColumnKey(product);
                  return (
                    <TableCell
                      key={product.id}
                      align="center"
                      sx={SX.featureValueCell}
                    >
                      <PlanCellValue
                        value={item.row.values[columnKey] ?? 'dash'}
                        subtext={item.row.subtexts?.[columnKey]}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

CollapsedTable.displayName = 'CollapsedTable';
